import { useState } from 'react';

import { dedupeSources, type Source } from './citations';
import type { Citation } from './types';

const MAX_VISIBLE = 5;

const FaviconChip = ({ source }: { source: Source }) => {
  const [broken, setBroken] = useState(false);

  return (
    <span
      title={source.host}
      aria-label={`Source: ${source.host}`}
      className="yst-relative -yst-ml-1.5 yst-flex yst-h-5 yst-w-5 yst-items-center yst-justify-center yst-rounded-full yst-bg-white yst-ring-1 yst-ring-slate-200 yst-transition first:yst-ml-0 hover:yst-z-10 hover:yst-scale-110 hover:yst-ring-slate-400"
    >
      {broken ? (
        <span aria-hidden="true" className="yst-text-[9px] yst-font-semibold yst-uppercase yst-text-slate-500">
          {source.host.replace(/^www\./, '')[0]}
        </span>
      ) : (
        <img
          // Google's favicon service resolves any host's icon without us
          // fetching the site directly; only the hostname is shared.
          src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(source.host)}&sz=32`}
          alt=""
          width={14}
          height={14}
          loading="lazy"
          className="yst-rounded-sm"
          onError={() => setBroken(true)}
        />
      )}
    </span>
  );
};

/**
 * Gemini-style source row: a line of slightly overlapping favicon chips (think
 * Audi rings) under the assistant reply showing the distinct sites the
 * research actually used — the first five, with an ellipsis chip (remaining
 * hostnames on hover) when there are more. Hostname on hover; deliberately not
 * clickable so users don't navigate away mid-onboarding. Renders nothing when
 * there are no (safe) sources.
 */
const SourceFavicons = ({ citations }: { citations: Citation[] }) => {
  const sources = dedupeSources(citations);
  if (sources.length === 0) return null;

  const visible = sources.slice(0, MAX_VISIBLE);
  const overflow = sources.slice(MAX_VISIBLE);

  return (
    <div className="yst-mt-2 yst-flex yst-items-center yst-gap-1.5" aria-label="Sources">
      <span className="yst-text-[10px] yst-text-slate-400">Sources</span>
      <span className="yst-flex yst-items-center">
        {visible.map((source) => (
          <FaviconChip key={source.host} source={source} />
        ))}
        {overflow.length > 0 && (
          <span
            title={overflow.map((s) => s.host).join(', ')}
            aria-label={`${overflow.length} more sources`}
            className="yst-relative -yst-ml-1.5 yst-flex yst-h-5 yst-w-5 yst-items-center yst-justify-center yst-rounded-full yst-bg-slate-100 yst-text-[9px] yst-font-medium yst-text-slate-500 yst-ring-1 yst-ring-slate-200 yst-transition hover:yst-z-10 hover:yst-scale-110 hover:yst-ring-slate-400"
          >
            …
          </span>
        )}
      </span>
    </div>
  );
};

export default SourceFavicons;
